/*
 Minimal Measurement Management list page
*/
import { useEffect, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import { Tooltip, IconButton } from "@mui/material";

// components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { useNavigate } from "react-router-dom";
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";

import CrudService from "services/cruds-service";

function MeasurementManagement() {
  const [rows, setRows] = useState([]);
  const [updated, setUpdated] = useState(false);
  const [notification, setNotification] = useState({ value: false, text: "" });
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = notification.value
      ? setTimeout(() => setNotification({ value: false, text: "" }), 4000)
      : null;
    return () => timer && clearTimeout(timer);
  }, [notification]);

  useEffect(() => {
    (async () => {
      try {
        const res = await CrudService.getMeasurements();
        // res.data is expected array of measurements JSON:API resources
        const list = (res.data || []).map((m) => ({
          id: m.id,
          weight: m.attributes?.weight,
          wrist: m.attributes?.wrist,
          arms: m.attributes?.arms,
          chest: m.attributes?.chest,
          waist: m.attributes?.waist,
          hips: m.attributes?.hips,
          neck: m.attributes?.neck,
          thighs: m.attributes?.thighs,
          unit: m.attributes?.unit,
          created_at: m.attributes?.created_at || m.attributes?.date_at || "",
        }));
        setRows(list);
      } catch (e) {
        console.error(e);
        setNotification({ value: true, text: e?.errors?.[0]?.detail || "Failed to load measurements" });
      }
    })();
  }, [updated]);

  const clickAddHandler = () => navigate("/measurement-management/new-measurement");
  const clickEditHandler = (id) => navigate(`/measurement-management/edit-measurement/${id}`);

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm("Are you sure you want to delete this measurement?")) {
        e?.nativeEvent?.stopImmediatePropagation?.();
        return;
      }
      await CrudService.deleteMeasurement(id);
      setUpdated((s) => !s);
      setNotification({ value: true, text: "Measurement deleted" });
    } catch (err) {
      console.error(err);
      setNotification({ value: true, text: err?.errors?.[0]?.detail || "Delete failed" });
    }
  };

  const table = {
    columns: [
      { Header: "weight", accessor: "weight", width: "10%" },
      { Header: "wrist", accessor: "wrist", width: "10%" },
      { Header: "arms", accessor: "arms", width: "10%" },
      { Header: "chest", accessor: "chest", width: "10%" },
      { Header: "waist", accessor: "waist", width: "10%" },
      { Header: "hips", accessor: "hips", width: "10%" },
      { Header: "neck", accessor: "neck", width: "10%" },
      { Header: "thighs", accessor: "thighs", width: "10%" },
      { Header: "unit", accessor: "unit", width: "5%" },
      { Header: "created at", accessor: "created_at", width: "15%" },
      {
        Header: "actions",
        accessor: "",
        disableSortBy: true,
        Cell: (info) => (
          <MDBox display="flex" alignItems="center">
            {ability.can("delete", "measurements") && (
              <Tooltip title="Delete" onClick={(e) => clickDeleteHandler(e, info.cell.row.original.id)}>
                <IconButton>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {ability.can("edit", "measurements") && (
              <Tooltip title="Edit" onClick={() => clickEditHandler(info.cell.row.original.id)}>
                <IconButton>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </MDBox>
        ),
      },
    ],
    rows,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">{notification.text}</MDTypography>
        </MDAlert>
      )}
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
              <MDTypography variant="h5" fontWeight="medium">
                Measurement Management
              </MDTypography>
              {ability.can("create", "measurements") && (
                <MDButton variant="gradient" color="dark" size="small" onClick={clickAddHandler}>
                  + Add Measurement
                </MDButton>
              )}
            </MDBox>
            <DataTable table={table} />
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MeasurementManagement;
